/*
util.ts
Copyright © 2026 Xerox Corporation. All Rights Reserved.

Copyright protection claimed includes all forms and matters of copyrightable material
and information now allowed by statutory or judicial law or hereinafter granted,
including without limitation, material generated from the software programs which
are displayed on the screen such as icons, screen display looks, etc.
*/

/* eslint-disable no-extra-boolean-cast */
declare const ExitCUIMode;
// Type for the response of `getUrlSearchParamsAsObj`
interface URLSearchParamsObject {
  [key: string]: string;
}

const blockedObjectKeys = new Set(['__proto__', 'prototype', 'constructor']);

const isSafeObjectKey = (key: string): boolean => {
  return !blockedObjectKeys.has(key);
};

// Device URL, considering the `import.meta.env.VITE_APP_DEBUG_DEVICE_IP` environment variable
export const deviceUrl: string  = location.port === "5173" ? import.meta.env.VITE_APP_DEBUG_DEVICE_IP : null;

// Generate a UUID
export const generateUuid = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0,
      v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Parse a JWT token
export const parseJwt = (token: string): object => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(window.atob(base64).split('').map((c) => {
    return `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`;
  }).join(''));

  return JSON.parse(jsonPayload);
};

// Convert an object to URLSearchParams
export const getSearchParams = (paramsObj: { [key: string]: string }): URLSearchParams => {
  const result = new URLSearchParams();
  Object.keys(paramsObj).forEach(k => {
    result.append(k, paramsObj[k]);
  });
  return result;
};

// Convert URL search parameters to an object
export const getUrlSearchParamsAsObj = (): URLSearchParamsObject => {
  const queryStringParameters = new URLSearchParams(window.location.search);
  const entries = queryStringParameters.entries();
  // Use a null-prototype object and guard keys to prevent prototype pollution.
  const obj = Object.create(null) as URLSearchParamsObject;
  for (let i = entries.next(); !i.done; i = entries.next()) {
    const [key, value] = i.value;
    const normalizedKey = key.toLowerCase();
    if (!isSafeObjectKey(normalizedKey)) {
      continue;
    }
    Object.defineProperty(obj, normalizedKey, {
      value: decodeURIComponent(value),
      enumerable: true,
      configurable: true,
      writable: true
    });
  }
  return obj;
};

// Deep copy an object
export const deepCopy = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

// Exit the application (if possible)
export const exitApp = (): void => {
  try {
    ExitCUIMode();
  } catch (error) {
    console.log(error)
    console.log("ExitCUIMode() function not found");
    location.href = "about:blank";
  }
};

// Check if the screen width is less than 801px (for 5-inch devices)
export const is5inches: boolean = screen.width < 801;



// Close bracket function, updates UI elements with class `ui-keyboard-58033`
export function closeBracketFunc(): void {
  const closeBracketArr = document.getElementsByClassName('ui-keyboard-58033');
  for (let i = 0; i < closeBracketArr.length; i++) {
    const closeBracket = closeBracketArr[i] as HTMLElement;
    if (closeBracket) {
      closeBracket.attributes.getNamedItem('data-value')!.textContent = '}';
      closeBracket.attributes.getNamedItem('data-action')!.textContent = '}';
      closeBracket.attributes.getNamedItem('data-name')!.textContent = '}';
      closeBracket.attributes.getNamedItem('data-html')!.textContent = `<span class="ui-keyboard-text">}</span>`;
      if (closeBracket?.firstChild) {
        closeBracket.firstChild.textContent = '}';
      }
    }
  }
}

// Check if a term contains any of the specified values
export const hasAnyValue = (term: string, values: string[]): boolean => {
  return values.some(value => term.includes(value));
};

export const validateEmailAddress = (email) => {
  return email.match(
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
};

export const getPropertyByName = (object: object, key: string) => {
  return object[Object.keys(object).filter(function(k) {
    return k.toLowerCase() === key.toLowerCase();
  })[0]];
}

export const alphanumericSort = (a, b) => a.localeCompare(b, 'en', { numeric: true })

export const langCode = navigator.language.split('-')[0];
export const countryCode = navigator.language.split('-')[1] ?? "us";
